import styled, { useTheme } from 'styled-components'
import { useEffect, useRef, useState, useMemo } from 'react'
import useScrollStore from '../useScrollStore'
import { useGetSize } from 'Hooks/useGetSize'
import * as d3 from 'd3'
import { useMediaQuery } from 'styled-breakpoints/use-media-query'

// End points of each stage
const stages = [0.05, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9]
const seasonOpacity = [0.16, 0.25, 0.4, 0.55]

const findStageIndex = (scrollValue: number, stages: number[]) => {
  const index = stages.findIndex((stage) => stage >= scrollValue)
  return index
}

const drawLine = (pct: number, length: number, path: SVGPathElement) => {
  if (!path) return
  const delta = 0.01
  const pathLength = path.getTotalLength()
  const coordinates = path.getPointAtLength(pathLength * pct)
  const pointBefore = path.getPointAtLength(pathLength * pct - delta)
  const pointAfter = path.getPointAtLength(pathLength * pct + delta)
  const slope = (pointAfter.y - pointBefore.y) / (pointAfter.x - pointBefore.x)

  // Calculate the slope of the perpendicular line
  const perpendicularSlope = -1 / slope

  // Check the direction of the curvature
  const isCurvingDownwards = pointAfter.y > pointBefore.y

  // Adjust the angle based on the curvature direction
  const angle =
    Math.atan(perpendicularSlope) + (isCurvingDownwards ? Math.PI : 0)

  return {
    x1: coordinates.x + 12 * Math.cos(angle),
    y1: coordinates.y + 12 * Math.sin(angle),
    x2: coordinates.x + length * Math.cos(angle),
    y2: coordinates.y + length * Math.sin(angle),
  }
}

const IntroSeasons = () => {
  const [markers, setMarkers] = useState<any>([])
  const theme = useTheme()
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const pathRef = useRef<SVGPathElement>(null)
  const pathRefIn = useRef<SVGPathElement>(null)
  const pathRefOut = useRef<SVGPathElement>(null)
  const { width } = useGetSize(containerRef)
  const height = width * 0.35
  const { breakpoints } = useTheme()
  const isMobile = useMediaQuery(breakpoints.down('md'))
  const pathWidth = isMobile ? 40 : 50
  const pathLength = pathRef.current?.getTotalLength() || 0
  const pathLengthIn = pathRefIn.current?.getTotalLength() || 0
  const pathLengthOut = pathRefOut.current?.getTotalLength() || 0

  const start = { x: pathWidth, y: height - pathWidth }
  const end = { x: width - pathWidth, y: height - pathWidth }
  const controPointA = { x: width / 3, y: height * 0.01 }
  const controPointB = { x: (width / 3) * 2, y: height * 0.01 }
  const path = `M${start.x},${start.y} C${controPointA.x},${controPointA.y} ${controPointB.x},${controPointB.y} ${end.x},${end.y}`
  const pathOut = `M${start.x -25},${start.y -25} C${controPointA.x -25},${controPointA.y -40} ${controPointB.x +25},${controPointB.y -40} ${end.x + 25},${end.y -25}`
  const pathIn = `M${start.x +33},${start.y +33} C${controPointA.x + 25},${controPointA.y + 55} ${controPointB.x - 25},${controPointB.y + 55} ${end.x - 33},${end.y + 33}`
  const seasons = [
    { name: 'printemps', start: 0, end: 0.25 },
    { name: 'été', start: 0.25, end: 0.51 },
    { name: 'automne', start: 0.51, end: 0.76 },
    { name: 'hiver', start: 0.76, end: 1 },
  ]

  const [stage, setStage] = useState<number>(0)

  useEffect(() => {
    const unsubscribe = useScrollStore.subscribe((state) => {
      const newStage = findStageIndex(state.scrollPct, stages)
      if (stage !== newStage && newStage >= 0) {
        setStage(newStage)
      }
    })

    return () => unsubscribe()
  }, [stage])

  useEffect(() => {
    d3.csv<any>(
      `${
        process.env.NODE_ENV === 'production'
          ? 'https://dev-interaktiv-mf.tagesanzeiger.ch/2023/seasons'
          : ''
      }/calendar.csv`,
      (d: any) => {
        return {
          paramId: +d['param_id'],
          doy2008: +d['doy_2008'],
          doy2023: +d['doy_2023'],
          pct2008: +d['doy_2008_pct_adjusted'],
          pct2023: +d['doy_2023_pct_adjusted'],
          date2008: new Date(d['date_2008']),
          date2023: new Date(d['date_2023']),
        }
      }
    )
      .then((data: any) => {
        setMarkers(data)
      })
      .catch((error: any) => {
        console.error('Error fetching the CSV data:', error)
      })
  }, [])

  const circles = useMemo(
    () => [
      ...markers.map((m: any) => ({ pct: m['pct2023'], date: m['date2023'] })),
      ...markers.map((m: any) => ({ pct: m['pct2008'], date: m['date2008'] })),
    ],
    [markers]
  )

  const Circles = circles.map((c: any, i: number) => {
    console.log(c.pct)
    const pos = pathRef.current?.getPointAtLength(c.pct * pathLength) || {
      x: 0,
      y: 0,
    }

    return (
      <Circle
        style={{
          opacity: i + 1 <= stage ? 1 : 0,
          background: `rgb(${
            i < 4 ? theme.greenColorBright : theme.yellowColor
          })`,
          position: 'absolute',
          left: pos.x,
          top: pos.y,
        }}
      />
    )
  })

  const links = useMemo(
    () =>
      markers.reduce((acc: any, m: any) => {
        const pStart = m['pct2008'] > m['pct2023'] ? m['pct2023'] : m['pct2008']
        const pEnd = m['pct2008'] > m['pct2023'] ? m['pct2008'] : m['pct2023']

        const segmentStartPoint = pathRef.current?.getPointAtLength(
          pStart * pathLength
        ) || { x: 0, y: 0 }
        const segmentEndPoint = pathRef.current?.getPointAtLength(
          pEnd * pathLength
        ) || { x: 0, y: 0 }

        const distance = Math.sqrt(
          (segmentEndPoint.x - segmentStartPoint.x) ** 2 +
            (segmentEndPoint.y - segmentStartPoint.y) ** 2
        )

        if (distance > pathLength * 0.5) {
          return [...acc, [pEnd, 1], [0, pStart]]
        }

        return [...acc, [pStart, pEnd]]
      }, []),
    [pathLength, markers]
  )

  const Links = links.map((m: any, i: number) => {
    const len = Math.abs(m[1] - m[0])

    return (
      <Link
        strokeDasharray={`${pathLength * len} ${pathLength * (1 - len)}`}
        strokeDashoffset={(1 - m[0]) * pathLength}
        d={path}
        stroke={`rgb(${theme.yellowColor})`}
        strokeWidth={10}
        fill='none'
        opacity={
          [0, 1].includes(i) && stage >= 5
            ? 1
            : ![0, 1].includes(i) && i + 4 <= stage
            ? 1
            : 0
        }
      />
    )
  })

  console.log(markers)
  console.log(circles)

  return (
    <Container ref={containerRef}>
      <Legend>
        <LegendBloc
          style={{
            maxWidth: stage >= 5 ? '100px' : 0,
            opacity: stage >= 5 ? 1 : 0,
          }}
        >
          <LegendCircle style={{ background: `rgb(${theme.yellowColor})` }} />{' '}
          2008
        </LegendBloc>
        <LegendBloc>
          <LegendCircle
            style={{ background: `rgb(${theme.greenColorBright})` }}
          />{' '}
          2023
        </LegendBloc>
      </Legend>
      <SvgContainer>
        <Svg width={width} height={height} ref={svgRef}>
          <path
            d={path}
            fill='none'
            stroke='green'
            strokeWidth={0}
            ref={pathRef}
          />
          <path
            id='TextPathOutside'
            d={pathOut}
            fill='none'
            stroke='red'
            strokeWidth={0}
            ref={pathRefOut}
          />
          <path
            id='TextPathInside'
            d={pathIn}
            fill='none'
            stroke='yellow'
            strokeWidth={0}
            ref={pathRefIn}
          />
          {seasons.map((season, i) => {
            const len = season.end - season.start

            return (
              <Path
                strokeDasharray={`${pathLength * len} ${
                  pathLength * (1 - len)
                }`}
                strokeDashoffset={(1 - season.start) * pathLength}
                d={path}
                strokeWidth={pathWidth}
                opacity={seasonOpacity[i]}
              />
            )
          })}
          <text>
            <PathLabel
              startOffset={
                pathLengthOut * (seasons[0].end - seasons[0].start) / 2
              }
              xlinkHref='#TextPathOutside'
              textAnchor={'middle'}
            >
              Printemps
            </PathLabel>
            <PathLabel
              startOffset={
                pathLengthOut * (seasons[3].start + (seasons[3].end - seasons[3].start) / 2) }
                xlinkHref='#TextPathOutside'
              textAnchor={'middle'}
            >
              Hiver
            </PathLabel>
          </text>
          {Links}
          {circles.map((m: any, i: number) => {
            return (
              <g style={{ opacity: i + 1 === stage ? 1 : 0 }}>
                <line
                  {...drawLine(m.pct, 30, pathRef.current!)}
                  stroke={`rgb(${theme.textColorOnBackground})`}
                />
                <text>
                  <PathLabel
                    startOffset={
                      pathLengthIn * m.pct
                    }
                    xlinkHref='#TextPathInside'
                    textAnchor={stage === 5 ? 'end' : 'middle'}
                  >
                    {m.date.toLocaleDateString('fr-CH', {
                      month: 'short',
                      day: 'numeric',
                    }
                    )}
                  </PathLabel>
                </text>
              </g>
            )
          })}
        </Svg>
        {Circles}
      </SvgContainer>
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  color: ${({ theme }) => `rgb(${theme.textColorOnBackground})`};
`

const SvgContainer = styled.div`
  position: relative;
`

const Svg = styled.svg`
  display: block;
`

const Path = styled.path`
  stroke: ${({ theme }) => `rgb(${theme.greenColorBright})`};
  fill: none;
`

const Circle = styled.div`
  display: inline-block;
  width: 15px;
  height: 15px;
  border: 1px solid ${({ theme }) => `rgb(${theme.coloredBackground})`};
  border-radius: 50%;
  top: 0;
  left: 0;
  transform: translate(-50%, -50%);
  // box-shadow: 1px 1px 7px rgba(0, 0, 0, 0.4);
  transition: opacity 0.3s ease;
`

const Link = styled.path`
  transition: opacity 0.3s ease;
`

const PathLabel = styled('textPath')<{
  startOffset: number;
  xlinkHref: string;
  textAnchor?: string;
  children?: React.ReactNode;
}>`
  fill: ${({ theme }) => `rgb(${theme.textColorOnBackground})`};
  font-size: ${({ theme }) => theme.textS};
`

const LegendCircle = styled(Circle)`
  transform: none;
  //width: 15px;
  //height: 15px;
  margin-right: 5px;
`

const Legend = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.textS};
`

const LegendBloc = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  transition: all 0.4s ease-in-out;
  overflow: hidden;

  &:last-child {
    margin-right: 0;
  }
`

export default IntroSeasons
